import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  creerUnDossier,
  deposerDesFichiers,
  lienDuFichier,
  modifierUnDossier,
  partagerUnDossier,
  relireLespace,
  retirerUnPartage,
  supprimerUnDossier,
  supprimerUnFichier,
} from '@groupe-trousseau/shared/api-client';
import { deballer } from '../../shared/api/client';
import { CLES } from '../../shared/donnees/cles';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'ESPACE DE FICHIERS — `CDC-3` F-33 À F-37.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **CE MODULE ÉTAIT BLOQUÉ, ET LE BLOCAGE EST LEVÉ.** D-009 écrit : « une seule retombée
 * bloquante ici, **le correctif `multer` par `@nestjs/platform-express`**, déni de service
 * et nettoyage incomplet des uploads avortés, **sur le chemin exact du Drive**. Il **bloque
 * l'ouverture du Drive documentaire** et entre à ce titre dans les prérequis externes. »
 * Constaté le 20260823 : l'arbre porte **`multer@2.2.0`**, tiré par
 * `@nestjs/platform-express@11.2.1`, et **`npm audit` ne rapporte plus rien sur `multer`**
 * — ni en développement, ni en production. Le prérequis est satisfait.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **UNE SEULE LECTURE REND TOUT L'ESPACE, ET C'EST UN CHOIX DU CONTRAT.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `relireLespace` rend **l'arborescence entière**, ses dossiers reçus, le poids courant et
 * le plafond. Il n'y a **aucune pagination**, et il ne faut pas en ajouter une côté front :
 * la profondeur est illimitée (Q439) mais le volume est borné par le plafond de la
 * personne, et **une navigation dossier par dossier coûterait un aller-retour par clic**
 * sur une surface qui s'ouvre en panneau à côté d'un tableau pendant un cours.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CETTE FEATURE N'EXPOSE PAS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * - **Aucun export de l'espace entier** (Q459). La route n'existe pas.
 * - **Aucune adresse publique.** « Rien du Drive n'atteint quelqu'un qui n'a pas de compte,
 *   autrement que par un fichier qu'une personne télécharge et transmet elle-même. » Le
 *   lien rendu par `lienDuFichier` est **présigné et à durée courte**, et il se demande au
 *   moment du clic — le garder en cache en ferait une adresse qui traîne.
 * - **Aucune corbeille.** « La suppression est immédiate. Aucun délai, aucune
 *   récupération » — et c'est « **le premier geste destructif qu'une personne exerce sur
 *   ses propres données sans qu'un tiers intervienne** ».
 * - **Aucune ligne de trace, sur aucune action** (Q548). Rien à écrire, donc rien à
 *   appeler.
 */

export function useEspace() {
  return useQuery({
    queryKey: CLES.espace,
    queryFn: () => deballer(relireLespace()),
  });
}

/** Tout geste sur l'espace change le poids affiché : une seule clé à invalider. */
function useGesteSurLEspace<Entree, Sortie>(
  appel: (entree: Entree) => Promise<Sortie>,
) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: appel,
    onSuccess: () => cache.invalidateQueries({ queryKey: CLES.espace }),
  });
}

export function useCreerUnDossier() {
  return useGesteSurLEspace((demande: { nom: string; parentId: string | null }) =>
    deballer(creerUnDossier({ body: demande })),
  );
}

/**
 * **Renommer et déplacer sont le MÊME geste côté contrat**, et c'est juste : les deux
 * changent une propriété du dossier, et rien d'autre. Deux routes auraient donné deux
 * chemins pour le même effet.
 */
export function useModifierUnDossier() {
  return useGesteSurLEspace(
    (demande: { id: string; nom?: string; parentId?: string | null }) =>
      deballer(
        modifierUnDossier({
          path: { id: demande.id },
          body: {
            ...(demande.nom === undefined ? {} : { nom: demande.nom }),
            ...(demande.parentId === undefined
              ? {}
              : { parentId: demande.parentId }),
          },
        }),
      ),
  );
}

export function useSupprimerUnDossier() {
  return useGesteSurLEspace((id: string) =>
    deballer(supprimerUnDossier({ path: { id } })),
  );
}

export function useSupprimerUnFichier() {
  return useGesteSurLEspace((id: string) =>
    deballer(supprimerUnFichier({ path: { id } })),
  );
}

/**
 * **Déposer un LOT, et le patron est la création partielle avec signalement** (F-34).
 *
 * « Quand un fichier du lot est refusé, **les autres entrent**, et le produit nomme ceux
 * qui n'ont pas pu, avec leur raison. **C'est le patron de la création partielle avec
 * signalement, et non celui de l'atomicité** : des fichiers choisis ensemble ne sont pas
 * indissociables. »
 *
 * La route répond donc `207`, et **la réponse porte deux listes**. Un écran qui n'en
 * lirait qu'une ferait disparaître des refus en silence, ce qui est exactement ce que le
 * patron existe pour empêcher.
 */
export function useDeposerDesFichiers() {
  return useGesteSurLEspace(
    (demande: { dossierId: string; fichiers: readonly File[] }) =>
      deballer(
        deposerDesFichiers({
          path: { id: demande.dossierId },
          body: { fichiers: [...demande.fichiers] },
        }),
      ),
  );
}

export function usePartagerUnDossier() {
  return useGesteSurLEspace(
    (demande: { dossierId: string; destinatairesIds: readonly string[] }) =>
      deballer(
        partagerUnDossier({
          path: { id: demande.dossierId },
          body: { destinatairesIds: [...demande.destinatairesIds] },
        }),
      ),
  );
}

export function useRetirerUnPartage() {
  return useGesteSurLEspace((partageId: string) =>
    deballer(retirerUnPartage({ path: { id: partageId } })),
  );
}

/**
 * **Le lien d'un fichier, demandé AU MOMENT DU CLIC et jamais avant.**
 *
 * `secure-file-uploads` : le bucket n'est jamais public, l'accès passe par **une URL
 * présignée à durée courte**. Précharger les liens de tous les fichiers d'un dossier en
 * ferait autant d'adresses valides qui traînent dans la mémoire d'un onglet, pour des
 * fichiers que personne n'ouvrira.
 *
 * **Ce n'est donc pas un `useQuery`** : une requête ne se déclenche pas à l'affichage,
 * elle se déclenche au geste.
 */
export function useOuvrirUnFichier() {
  return useMutation({
    mutationFn: (fichierId: string) =>
      deballer(lienDuFichier({ path: { id: fichierId } })),
  });
}
